import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/common/BrandLogo';
import RegistrationAgreement from '../../components/auth/RegistrationAgreement';
import { MIN_PASSWORD_LENGTH } from '../../constants/password';
import { useAuth } from '../../context/AuthContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!agreementAccepted) {
      setError('Debes aceptar el acuerdo de registro para continuar.');
      return;
    }

    setSubmitting(true);

    try {
      const data = await signUp({ fullName, email, password, agentCode, agreementAccepted });
      if (data.session) {
        navigate('/app');
      } else {
        setSuccess('Cuenta creada. Revisa tu email para confirmar el registro y luego inicia sesión.');
      }
    } catch (err) {
      setError(err.message || 'No se pudo completar el registro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <BrandLogo size="lg" />
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-3xl p-8 shadow-sm">
          <h1 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2 text-center">
            Sumarme al Movimiento
          </h1>
          <p className="text-xs text-[var(--text-secondary)] text-center mb-8 uppercase tracking-widest">
            Crea tu cuenta de miembro GFI Latino
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                Nombre completo
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                Contraseña
              </label>
              <input
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                Código de Agente <span className="text-brand-gold-dark">*</span>
              </label>
              <input
                type="text"
                required
                value={agentCode}
                onChange={(e) => setAgentCode(e.target.value.toUpperCase())}
                placeholder="Ej: ML-12345"
                className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm font-bold text-[var(--text-main)] uppercase focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
              />
              <p className="text-[10px] text-[var(--text-secondary)] mt-2 leading-relaxed">
                Si el código ya está en uso, se te asignará uno temporal y un administrador lo revisará.
              </p>
            </div>

            <RegistrationAgreement
              accepted={agreementAccepted}
              onAcceptedChange={setAgreementAccepted}
              disabled={submitting}
            />

            {error && <p className="text-sm text-rose-600 font-medium">{error}</p>}
            {success && <p className="text-sm text-brand-green font-medium">{success}</p>}

            <button
              type="submit"
              disabled={submitting || !agreementAccepted}
              className="w-full gfi-gradient-bg text-brand-navy py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-60"
            >
              {submitting ? 'Creando cuenta...' : 'Crear cuenta'}
            </button>
          </form>

          <p className="text-center text-xs text-[var(--text-secondary)] mt-8">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-brand-cyan font-bold hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
